/** @format */

import { type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { CmsCardListCard } from 'src/types';

interface CardListProps {
  cards: CmsCardListCard[];
}

export const filterCardList = (cards: CmsCardListCard[], query: string): CmsCardListCard[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return cards;

  return cards.filter(card => {
    const haystack = [card.title, card.subtitle, card.teaser].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(normalized);
  });
};

const downloadUrlOf = (card: CmsCardListCard): string | undefined => {
  const candidates = [card.download?.link, card.downloadUrl, card.downloadLink, card.fileUrl];
  return candidates.find(value => typeof value === 'string' && value.trim())?.trim();
};

const fileNameOf = (url: string): string => {
  const path = url.split('?')[0].split('#')[0];
  const name = path.split('/').pop() || '';
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const saveRemoteFile = async (url: string): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) return;

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileNameOf(url);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const detailHrefOf = (card: CmsCardListCard): string | undefined => {
  if (card.nid) return `/page/${card.nid}`;
  const link = card.link?.trim();
  return link || undefined;
};

const ActionLink = ({
  href,
  className,
  children,
  backTo,
  label
}: {
  href: string;
  className: string;
  children: ReactNode;
  backTo?: string;
  label?: string;
}) => {
  if (/^https?:\/\//.test(href)) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={href} state={backTo ? { backTo } : undefined} aria-label={label}>
      {children}
    </Link>
  );
};

const CardListItem = ({ card }: { card: CmsCardListCard }) => {
  const location = useLocation();
  const detailHref = detailHrefOf(card);
  const linkTitle = card.linkTitle?.trim();
  const backTo = `${location.pathname}${location.search}`;
  const downloadUrl = downloadUrlOf(card);
  const downloadLabel = card.downloadButtonLabel?.trim();
  const handleDownload = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!downloadUrl) return;
    event.preventDefault();
    void saveRemoteFile(downloadUrl);
  };

  return (
    <article className="card-list__card">
      <div className="card-list__body">
        {card.image ? (
          <div className="card-list__media">
            {detailHref ? (
              <ActionLink href={detailHref} className="card-list__media-link" backTo={backTo} label={card.title}>
                <img src={card.image} alt="" />
              </ActionLink>
            ) : (
              <img src={card.image} alt="" />
            )}
          </div>
        ) : null}
        <div className="card-list__copy">
          <h2 className="card-list__title">
            {detailHref ? (
              <ActionLink href={detailHref} className="card-list__title-link" backTo={backTo}>
                {card.title}
              </ActionLink>
            ) : (
              card.title
            )}
          </h2>
          {card.subtitle ? <p className="card-list__subtitle">{card.subtitle}</p> : null}
          {card.teaser ? <p className="card-list__teaser">{card.teaser}</p> : null}
        </div>
      </div>
      {(detailHref && linkTitle) || downloadLabel ? (
        <div className="card-list__actions">
          {detailHref && linkTitle ? (
            <ActionLink href={detailHref} className="button secondary text small" backTo={backTo}>
              {linkTitle}
            </ActionLink>
          ) : null}
          {downloadLabel ? (
            downloadUrl ? (
              <a
                className="button primary small card-list__download"
                href={downloadUrl}
                download={fileNameOf(downloadUrl)}
                onClick={handleDownload}>
                {downloadLabel}
              </a>
            ) : (
              <button type="button" className="button primary small card-list__download">
                {downloadLabel}
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export const CardList = ({ cards }: CardListProps) => {
  if (!cards.length) return null;

  return (
    <div className="card-list">
      {cards.map(card => (
        <CardListItem key={card.id} card={card} />
      ))}
    </div>
  );
};

export const isCardListContent = (content: { type?: string; cards?: unknown } | null | undefined): boolean =>
  content?.type === 'card_list' && Array.isArray(content.cards);
